import type { ProductDto } from "../../../shared/api-types";
import type { Allergen, ProductLabel } from "../../../shared/constants";
import { productSchema } from "../../../shared/menu-schemas";
import { formatPriceInput } from "../../../shared/money";
import { CHIPS } from "../../components/kit";

// Product editor state (prototype `form` + `formErrors` + `saveEditor`), kept free of React so it can be tested.

export interface VariantRow {
  key: string;
  label: string;
  price: string;
}

export interface EditorForm {
  name: string;
  categoryId: string;
  mode: "single" | "variants";
  price: string;
  variants: VariantRow[];
  description: string;
  portion: string;
  ingredients: string;
  allergens: Allergen[];
  allergensConfirmed: boolean;
  labels: ProductLabel[];
  /** Made from a frozen product (the restaurant says so; the public menu shows it). */
  frozen: boolean;
  /** Uploaded photo (media id) or null. Uploads happen when the file is picked; saving only links it. */
  imageId: string | null;
  nameEn: string;
  descEn: string;
  ingrEn: string;
  trReviewed: boolean;
}

let seq = 0;
const key = () => `v${++seq}`;

export function emptyForm(categoryId: string): EditorForm {
  return {
    name: "",
    categoryId,
    mode: "single",
    price: "",
    variants: [
      { key: key(), label: "", price: "" },
      { key: key(), label: "", price: "" },
    ],
    description: "",
    portion: "",
    ingredients: "",
    allergens: [],
    allergensConfirmed: false,
    labels: [],
    frozen: false,
    imageId: null,
    nameEn: "",
    descEn: "",
    ingrEn: "",
    trReviewed: false,
  };
}

export function formFromProduct(p: ProductDto): EditorForm {
  const en = p.translations.en;
  return {
    name: p.name,
    categoryId: p.categoryId,
    mode: p.variants ? "variants" : "single",
    price: p.priceMinor !== null ? formatPriceInput(p.priceMinor) : "",
    variants: p.variants
      ? p.variants.map((v) => ({ key: key(), label: v.label, price: formatPriceInput(v.priceMinor) }))
      : [
          { key: key(), label: "", price: "" },
          { key: key(), label: "", price: "" },
        ],
    description: p.description,
    portion: p.portion,
    ingredients: p.ingredients,
    allergens: [...p.allergens],
    allergensConfirmed: p.allergensConfirmed,
    labels: [...p.labels],
    frozen: p.frozen,
    imageId: p.imageKey,
    nameEn: en?.name ?? "",
    descEn: en?.description ?? "",
    ingrEn: en?.ingredients ?? "",
    trReviewed: en?.status === "reviewed",
  };
}

/** The request body for POST/PUT …/products (validated again by the API). */
export function toProductInput(f: EditorForm) {
  return {
    categoryId: f.categoryId,
    name: f.name,
    description: f.description,
    ingredients: f.ingredients,
    portion: f.portion,
    pricing:
      f.mode === "single"
        ? { mode: "single" as const, price: f.price }
        : {
            mode: "variants" as const,
            variants: f.variants.map((v) => ({ label: v.label, price: v.price })),
          },
    allergens: f.allergens,
    allergensConfirmed: f.allergensConfirmed,
    labels: f.labels,
    frozen: f.frozen,
    imageId: f.imageId,
    translation: {
      lang: "en" as const,
      name: f.nameEn,
      description: f.descEn,
      ingredients: f.ingrEn,
      reviewed: f.trReviewed,
    },
  };
}

/** Field errors keyed like the API's (e.g. "pricing.variants.0.price"), with the prototype's messages. */
export function validateForm(f: EditorForm): Record<string, string> {
  const r = productSchema.safeParse(toProductInput(f));
  if (r.success) return {};
  const errors: Record<string, string> = {};
  for (const issue of r.error.issues) errors[issue.path.join(".") || "_"] ??= issue.message;
  return errors;
}

/** Changing the Romanian source text invalidates a "reviewed" mark on the translation (DEC-020). */
export function withSourceChange(
  f: EditorForm,
  patch: Partial<Pick<EditorForm, "name" | "description" | "ingredients">>,
) {
  return { ...f, ...patch, trReviewed: false };
}

/** Changing ingredients or allergens requires a new explicit confirmation (prototype `foodChanged`). */
export function withFoodChange(f: EditorForm, patch: Partial<Pick<EditorForm, "ingredients" | "allergens">>) {
  // Ingredients are also translated source text; allergens are not.
  const base = patch.ingredients !== undefined ? withSourceChange(f, { ingredients: patch.ingredients }) : f;
  return { ...base, ...patch, allergensConfirmed: false };
}

/** Translation status chip shown in the editor (prototype `trK`). */
export function translationChip(f: EditorForm, original?: ProductDto) {
  const en = original?.translations.en;
  const sourceChanged =
    !!original &&
    (original.name !== f.name.trim() ||
      original.description !== f.description.trim() ||
      original.ingredients !== f.ingredients.trim());
  const enChanged = original
    ? (en?.name ?? "") !== f.nameEn.trim() ||
      (en?.description ?? "") !== f.descEn.trim() ||
      (en?.ingredients ?? "") !== f.ingrEn.trim()
    : !!f.nameEn.trim();
  if (!f.nameEn.trim()) return CHIPS.trMissing;
  if (f.trReviewed) return { tone: "success", icon: CHIPS.published.icon, label: "EN · revizuită" } as const;
  if (enChanged)
    return { tone: "success", icon: CHIPS.published.icon, label: "EN · editată manual" } as const;
  if (sourceChanged || en?.status === "needs_update") return CHIPS.trNeeds;
  if (en?.status === "auto_unreviewed") return CHIPS.trAuto;
  return {
    tone: "success",
    icon: CHIPS.published.icon,
    label: en?.status === "reviewed" ? "EN · revizuită" : "EN · editată manual",
  } as const;
}

export const isDirty = (a: EditorForm, b: EditorForm) => {
  const strip = (f: EditorForm) => ({
    ...f,
    variants: f.variants.map((v) => ({ label: v.label, price: v.price })),
  });
  return JSON.stringify(strip(a)) !== JSON.stringify(strip(b));
};
